<template>
    <div class="roommate-flow-wrapper">
        <!-- PHONE NUMBER STEP -->
        <div v-if="mode === 'phone'" class="roommate-phone-card">
            <div v-if="phonePromptMessage" class="roommate-phone-message">
                {{ phonePromptMessage }}
            </div>
            <div class="roommate-phone-row">
                <Dropdown v-model="phoneForm.countryCodeId"
                          :options="normalizedCountryCodes"
                          optionValue="value"
                          optionLabel="text"
                          placeholder="Code"
                          filter
                          class="roommate-country-dropdown"
                          :disabled="loading || phoneSubmitted"
                          @change="onCountryCodeChange" />
                <input v-model="phoneForm.phoneNumber"
                       type="text"
                       inputmode="numeric"
                       class="roommate-phone-input"
                       :placeholder="phonePlaceholder"
                       :disabled="loading || phoneSubmitted"
                       @input="onPhoneInput"
                       @keyup.enter="submitPhone" />
            </div>
            <div v-if="phoneError" class="roommate-phone-error">
                {{ phoneError }}
            </div>
            <button type="button"
                    class="roommate-phone-submit"
                    :disabled="loading || phoneSubmitted || !isPhoneComplete"
                    @click="submitPhone">
                {{ loading ? 'Please wait...' : 'Continue' }}
            </button>
        </div>

        <!-- PREVIEW -->
        <div v-else-if="mode === 'preview'" class="roommate-card">
            <div class="roommate-card-header">
                <div>
                    <div class="roommate-card-title">{{ entityLabel }} Summary</div>
                    <div class="roommate-card-subtitle">
                        {{ message || 'Please review before adding' }}
                    </div>
                </div>
            </div>
            <div class="roommate-card-body">
                <div class="roommate-row">
                    <span class="roommate-label">First Name</span>
                    <span class="roommate-value">
                        {{ valueOrDash(prefilled.firstName) }}
                    </span>
                </div>
                <div class="roommate-row">
                    <span class="roommate-label">Last Name</span>
                    <span class="roommate-value">
                        {{ valueOrDash(prefilled.lastName) }}
                    </span>
                </div>
                <div class="roommate-row">
                    <span class="roommate-label">Email</span>
                    <span class="roommate-value">
                        {{ valueOrDash(prefilled.email) }}
                    </span>
                </div>
                <div class="roommate-row">
                    <span class="roommate-label">Country Code</span>
                    <span class="roommate-value">
                        {{ previewCountryCode }}
                    </span>
                </div>
                <div class="roommate-row">
                    <span class="roommate-label">Phone Number</span>
                    <span class="roommate-value">
                        {{ previewPhone }}
                    </span>
                </div>
                <div class="roommate-row no-border">
                    <span class="roommate-label">Asset</span>
                    <span class="roommate-value">
                        {{ valueOrDash(prefilled.assetName) }}
                    </span>
                </div>
            </div>
            <div class="roommate-card-actions">
                <button type="button"
                        class="btn-primary-roommate"
                        :disabled="loading || submitted"
                        @click="confirmAdd">
                    {{ loading ? 'Please wait...' : 'Confirm & Add' }}
                </button>
                <button type="button"
                        class="btn-cancel-roommate"
                        :disabled="loading"
                        @click="$emit('cancel')">
                    Cancel
                </button>
            </div>
        </div>

        <!-- LIST -->
        <div v-else-if="mode === 'list'"
             class="roommate-card roommate-list-card">
            <div class="roommate-card-header">
                <div>
                    <div class="roommate-card-title">
                        Your {{ entityLabelPlural }}
                    </div>
                    <div v-if="message"
                         class="roommate-card-subtitle">
                        {{ message }}
                    </div>
                </div>
                <div class="roommate-count roommate-count-orange">
                    {{ roommates.length }}
                </div>
            </div>
            <div v-if="roommates.length"
                 class="roommate-list">
                <div v-for="(roommate, index) in roommates"
                     :key="roommate.shareHistoryId || roommate.ShareHistoryId || roommate.id || index"
                     class="roommate-list-item">
                    <!-- ROOMMATE TOP ROW -->
                    <div class="roommate-list-top">
                        <div class="roommate-list-main">
                            <!-- AVATAR + NAME/BADGES -->
                            <div class="roommate-name-row">
                                <div class="roommate-avatar"
                                     :style="{ background: getAvatarColor(roommate, index) }">
                                    {{ getRoommateInitials(roommate) }}
                                </div>
                                <div class="roommate-name-info">
                                    <div class="roommate-name">
                                        {{ getRoommateName(roommate) }}
                                    </div>
                                    <div class="roommate-badges">
                                        <span v-if="isPrimaryRoommate(roommate)"
                                              class="primary-badge">
                                            Primary
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="roommate-list-actions">
                            <button type="button"
                                    class="roommate-details-btn"
                                    :disabled="loading"
                                    @click="toggleRoommateDetails(roommate, index)">
                                {{ isRoommateDetailsOpen(roommate, index) ? 'Hide' : 'Details' }}
                                <span class="roommate-details-arrow">
                                    {{ isRoommateDetailsOpen(roommate, index) ? '▲' : '▼' }}
                                </span>
                            </button>
                            <button v-if="!isPrimaryRoommate(roommate)"
                                    type="button"
                                    class="remove-roommate-btn"
                                    :disabled="loading"
                                    @click="requestDelete(roommate)">
                                Remove
                            </button>
                        </div>
                    </div>
                    <!-- ROOMMATE DETAILS -->
                    <div v-if="isRoommateDetailsOpen(roommate, index)"
                         class="roommate-details">
                        <div class="roommate-detail-row">
                            <span class="roommate-detail-label">
                                Email
                            </span>
                            <span class="roommate-detail-value">
                                {{ getRoommateEmail(roommate) }}
                            </span>
                        </div>
                        <div class="roommate-detail-row">
                            <span class="roommate-detail-label">
                                Phone
                            </span>
                            <span class="roommate-detail-value">
                                {{ getRoommatePhone(roommate) }}
                            </span>
                        </div>
                        <div class="roommate-detail-row">
                            <span class="roommate-detail-label">
                                Share
                            </span>
                            <span class="roommate-detail-value"
                                  v-html="getRoommateShare(roommate) || '-'">
                            </span>
                        </div>
                        <div class="roommate-detail-row">
                            <span class="roommate-detail-label">
                                Payment Date
                            </span>
                            <span class="roommate-detail-value">
                                {{ getRoommatePaymentDate(roommate) }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            <div v-else
                 class="empty-state">
                No {{ entityLabelPlural.toLowerCase() }} found.
            </div>
        </div>

        <!-- ROOMMATE CONFIRMATION POPUP -->
        <div v-if="confirmModal.show"
             class="roommate-confirm-overlay"
             @click.self="closeConfirmModal">
            <div class="roommate-confirm-card"
                 @click.stop>
                <div class="roommate-confirm-header">
                    {{ confirmModal.title }}
                </div>
                <div class="roommate-confirm-body">
                    <p class="roommate-confirm-message">
                        {{ confirmModal.message }}
                    </p>
                    <div class="roommate-confirm-actions">
                        <button type="button"
                                class="roommate-confirm-btn roommate-confirm-ok"
                                @click="acceptConfirmModal">
                            Ok
                        </button>
                        <button type="button"
                                class="roommate-confirm-btn roommate-confirm-cancel"
                                @click="closeConfirmModal">
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- SUCCESS / ERROR -->
        <div v-else-if="mode === 'success' || mode === 'error'"
             class="roommate-card">
            <div class="roommate-card-header"
                 :class="{ 'error-header': resultIsError }">
                <div>
                    <div class="roommate-card-title">
                        {{ resultIsError ? (entityLabel + ' Not Added') : (entityLabel + ' Added') }}
                    </div>
                </div>
            </div>
            <div class="roommate-card-body">
                <div class="roommate-row">
                    <span class="roommate-label">
                        Name
                    </span>
                    <span class="roommate-value">
                        {{ resultFullName }}
                    </span>
                </div>
                <div class="roommate-row">
                    <span class="roommate-label">
                        Email
                    </span>
                    <span class="roommate-value">
                        {{ valueOrDash(resultEmail) }}
                    </span>
                </div>
                <div v-if="resultCountryCode !== '-'"
                     class="roommate-row">
                    <span class="roommate-label">
                        Country Code
                    </span>
                    <span class="roommate-value">
                        {{ resultCountryCode }}
                    </span>
                </div>
                <div v-if="resultPhone !== '-'"
                     class="roommate-row no-border">
                    <span class="roommate-label">
                        Phone Number
                    </span>
                    <span class="roommate-value">
                        {{ resultPhone }}
                    </span>
                </div>
                <div v-if="resultIsError"
                     class="roommate-error">
                    {{ roommateData.error || roommateData.message || 'Please try again.' }}
                </div>
            </div>
            <div class="roommate-result-footer">
                <span>
                    {{ formattedToday }}
                </span>
                <span :class="resultIsError ? 'status-error' : 'status-success'">
                    {{ resultIsError ? 'Not Saved' : 'Saved' }}
                </span>
            </div>
        </div>
    </div>
</template>

<script>
    import Dropdown from 'primevue/dropdown';
    import { resolvePhoneLength } from '@/helpers/phoneCountryHelpers.js';

    export default {
        name: 'AddRoomate',
        components: {
            Dropdown
        },
        props: {
            mode: {
                type: String,
                default: 'preview'
            },
            message: {
                type: String,
                default: ''
            },
            roommateData: {
                type: Object,
                default: () => ({})
            },
            countryCodeList: {
                type: Array,
                default: () => []
            },
            selectedCountryCodeText: {
                type: String,
                default: ''
            },
            loading: {
                type: Boolean,
                default: false
            },
            isError: {
                type: Boolean,
                default: false
            }
        },
        emits: [
            'submit',
            'cancel',
            'delete-roommate',
            'toggle-status',
            'phone-submit'
        ],
        data() {
            return {
                phoneForm: {
                    countryCodeId: '',
                    countryCodeText: '',
                    phoneNumber: '',
                },
                phoneError: '',
                submitted: false,
                phoneSubmitted: false,
                expandedRoommateKey: '',
                avatarColorPalette: [
                    '#7c3aed',
                    '#2563eb',
                    '#dc2626',
                    '#d97706',
                    '#059669',
                    '#0891b2',
                    '#db2777',
                    '#4f46e5'
                ],
                confirmModal: {
                    show: false,
                    title: '',
                    message: '',
                    action: '',
                    roommate: null,
                    newStatus: null
                }
            };
        },
        computed: {
            // Detect asset type from localStorage
            assetTypeKey() {
                const t = String(localStorage.getItem('assetType') || '').trim().toLowerCase();
                if (t === 'boat' || t === 'boatdera') return 'boat';
                if (t === 'aircraft' || t === 'plane' || t === 'planedera') return 'aircraft';
                return 'default';
            },
            // Singular label
            entityLabel() {
                if (this.assetTypeKey === 'boat' || this.assetTypeKey === 'aircraft') {
                    return 'Co-owner';
                }
                return 'Roommate';
            },
            // Plural label
            entityLabelPlural() {
                if (this.assetTypeKey === 'boat' || this.assetTypeKey === 'aircraft') {
                    return 'Co-owners';
                }
                return 'Roommates';
            },
            phonePromptMessage() {
                return String(this.message || '')
                    .replace(/\*\*/g, '')
                    .trim();
            },
            requiredPhoneLength() {
                const resolvedLength = Number(
                    resolvePhoneLength(
                        this.phoneForm.countryCodeId,
                        this.countryCodeList || []
                    )
                );
                return resolvedLength > 0
                    ? resolvedLength
                    : 10;
            },
            phonePlaceholder() {
                return this.buildPhonePlaceholder(
                    Number(this.requiredPhoneLength || 10)
                );
            },
            isPhoneComplete() {
                return String(this.phoneForm.phoneNumber || '')
                    .replace(/\D/g, '')
                    .length === Number(this.requiredPhoneLength || 10);
            },
            prefilled() {
                return this.roommateData?.prefilled || {};
            },
            normalizedCountryCodes() {
                return (this.countryCodeList || [])
                    .map(item => {
                        const value =
                            item.value ??
                            item.id ??
                            item.Id ??
                            item.countryCodeId ??
                            item.CountryCodeId ??
                            '';
                        const text =
                            item.text ??
                            item.name ??
                            item.Name ??
                            item.countryCode ??
                            item.CountryCode ??
                            item.countryCodeText ??
                            item.CountryCodeText ??
                            '';
                        return {
                            value: String(value || ''),
                            text: String(text || '')
                        };
                    })
                    .filter(item => item.value || item.text);
            },
            selectedCountryCode() {
                if (!this.phoneForm.countryCodeId) {
                    return null;
                }
                return this.normalizedCountryCodes.find(
                    item =>
                        String(item.value) ===
                        String(this.phoneForm.countryCodeId)
                ) || null;
            },
            roommates() {
                if (Array.isArray(this.roommateData?.roommates)) {
                    return this.roommateData.roommates;
                }
                if (Array.isArray(this.roommateData?.data)) {
                    return this.roommateData.data;
                }
                if (Array.isArray(this.roommateData?.items)) {
                    return this.roommateData.items;
                }
                return [];
            },
            previewCountryCode() {
                let country = String(
                    this.prefilled.countryCodeText || ''
                ).trim();
                if (
                    !country ||
                    country === 'Loading…' ||
                    country === 'Loading...'
                ) {
                    country = String(
                        this.selectedCountryCodeText ||
                        this.selectedCountryCode?.text ||
                        this.phoneForm.countryCodeText ||
                        ''
                    ).trim();
                }
                return country || '-';
            },
            previewPhone() {
                let phone = String(
                    this.prefilled.phoneNumber || ''
                ).trim();
                if (!phone) {
                    return '-';
                }
                phone = phone
                    .replace(/^\+\d+\s*/, '')
                    .trim();
                return phone || '-';
            },
            resultIsError() {
                return this.isError || this.mode === 'error';
            },
            resultFullName() {
                const firstName =
                    this.roommateData?.firstName ||
                    this.roommateData?.prefilled?.firstName ||
                    '';
                const lastName =
                    this.roommateData?.lastName ||
                    this.roommateData?.prefilled?.lastName ||
                    '';
                const fullName =
                    `${firstName} ${lastName}`.trim();
                return fullName || '-';
            },
            resultEmail() {
                return (
                    this.roommateData?.email ||
                    this.roommateData?.prefilled?.email ||
                    ''
                );
            },
            resultCountryCode() {
                let country = String(
                    this.roommateData?.countryCodeText ||
                    this.roommateData?.prefilled?.countryCodeText ||
                    ''
                ).trim();
                if (
                    !country ||
                    country === 'Loading…' ||
                    country === 'Loading...'
                ) {
                    country =
                        String(this.selectedCountryCodeText || '').trim() ||
                        String(this.selectedCountryCode?.text || '').trim() ||
                        String(this.phoneForm.countryCodeText || '').trim();
                }
                return country || '-';
            },
            resultPhone() {
                let phone = String(
                    this.roommateData?.phoneNumber ||
                    this.roommateData?.prefilled?.phoneNumber ||
                    ''
                ).trim();
                if (!phone) {
                    return '-';
                }
                phone = phone
                    .replace(/^\+\d+\s*/, '')
                    .trim();
                return phone || '-';
            },
            formattedToday() {
                return new Date().toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                });
            }
        },
        watch: {
            roommateData: {
                immediate: true,
                deep: true,
                handler(value) {
                    const pre = value?.prefilled || {};
                    if (this.mode === 'phone') {
                        this.phoneSubmitted = false;
                    }
                    this.phoneForm.phoneNumber =
                        pre.phoneNumber ||
                        value?.phoneNumber ||
                        '';
                    const incomingCountryCodeId =
                        pre.countryCodeId ||
                        value?.countryCodeId ||
                        '';
                    if (incomingCountryCodeId) {
                        this.phoneForm.countryCodeId =
                            incomingCountryCodeId;
                    }
                    let countryText =
                        pre.countryCodeText ||
                        value?.countryCodeText ||
                        '';
                    if (
                        countryText === 'Loading…' ||
                        countryText === 'Loading...'
                    ) {
                        countryText = '';
                    }
                    if (countryText) {
                        this.phoneForm.countryCodeText =
                            countryText;
                    } else if (this.selectedCountryCodeText) {
                        this.phoneForm.countryCodeText =
                            this.selectedCountryCodeText;
                    }
                }
            },
            countryCodeList: {
                immediate: true,
                deep: true,
                handler() {
                    if (!this.normalizedCountryCodes.length) {
                        return;
                    }
                    if (this.phoneForm.countryCodeId) {
                        const existing =
                            this.normalizedCountryCodes.find(
                                item =>
                                    String(item.value) ===
                                    String(this.phoneForm.countryCodeId)
                            );
                        if (existing) {
                            this.phoneForm.countryCodeText =
                                this.phoneForm.countryCodeText ||
                                existing.text ||
                                '';
                            return;
                        }
                    }
                    const us =
                        this.normalizedCountryCodes.find(item =>
                            /\(US\)|\bUS\b/i.test(
                                String(item.text || '')
                            )
                        ) ||
                        this.normalizedCountryCodes.find(item =>
                            String(item.text || '')
                                .trim()
                                .startsWith('+1')
                        );
                    if (us) {
                        this.phoneForm.countryCodeId =
                            us.value;
                        this.phoneForm.countryCodeText =
                            us.text || '';
                    }
                }
            }
        },
        methods: {
            valueOrDash(value) {
                return value || '-';
            },
            onCountryCodeChange() {
                const selected =
                    this.normalizedCountryCodes.find(
                        item =>
                            String(item.value) ===
                            String(this.phoneForm.countryCodeId)
                    );
                this.phoneForm.countryCodeText =
                    selected?.text || '';
                this.phoneForm.phoneNumber = '';
                this.phoneError = '';
                this.phoneSubmitted = false;
            },
            buildPhonePlaceholder(length) {
                const requiredLength =
                    Number(length || 10);
                if (requiredLength === 10) {
                    return '(000)-000-0000';
                }
                if (requiredLength === 7) {
                    return '000-0000';
                }
                if (requiredLength === 8) {
                    return '0000-0000';
                }
                if (requiredLength === 9) {
                    return '000-000-000';
                }
                if (requiredLength === 11) {
                    return '000-0000-0000';
                }
                if (requiredLength > 0) {
                    return '0'.repeat(requiredLength);
                }
                return 'Phone number';
            },
            formatPhoneByLength(digits, length) {
                const clean =
                    String(digits || '')
                        .replace(/\D/g, '');
                const requiredLength =
                    Number(length || 10);
                if (requiredLength === 10) {
                    if (clean.length > 6) {
                        return `(${clean.slice(0, 3)})-${clean.slice(3, 6)}-${clean.slice(6)}`;
                    }
                    if (clean.length > 3) {
                        return `(${clean.slice(0, 3)})-${clean.slice(3)}`;
                    }
                    if (clean.length > 0) {
                        return `(${clean}`;
                    }
                    return '';
                }
                if (requiredLength === 7) {
                    return clean.length > 3
                        ? `${clean.slice(0, 3)}-${clean.slice(3)}`
                        : clean;
                }
                if (requiredLength === 8) {
                    return clean.length > 4
                        ? `${clean.slice(0, 4)}-${clean.slice(4)}`
                        : clean;
                }
                if (requiredLength === 9) {
                    if (clean.length > 6) {
                        return `${clean.slice(0, 3)}-${clean.slice(3, 6)}-${clean.slice(6)}`;
                    }
                    if (clean.length > 3) {
                        return `${clean.slice(0, 3)}-${clean.slice(3)}`;
                    }
                    return clean;
                }
                if (requiredLength === 11) {
                    if (clean.length > 7) {
                        return `${clean.slice(0, 3)}-${clean.slice(3, 7)}-${clean.slice(7)}`;
                    }
                    if (clean.length > 3) {
                        return `${clean.slice(0, 3)}-${clean.slice(3)}`;
                    }
                    return clean;
                }
                return clean;
            },
            onPhoneInput(event) {
                const requiredLength =
                    Number(this.requiredPhoneLength || 10);
                const digits =
                    String(event?.target?.value || '')
                        .replace(/\D/g, '')
                        .slice(0, requiredLength);
                this.phoneForm.phoneNumber =
                    this.formatPhoneByLength(
                        digits,
                        requiredLength
                    );
                this.phoneError = '';
            },
            submitPhone() {
                this.phoneError = '';
                if (this.phoneSubmitted) {
                    return;
                }
                if (!this.phoneForm.countryCodeId) {
                    this.phoneError =
                        'Please select a country code.';
                    return;
                }
                const requiredLength =
                    Number(this.requiredPhoneLength || 10);
                const digits =
                    String(this.phoneForm.phoneNumber || '')
                        .replace(/\D/g, '');
                if (digits.length !== requiredLength) {
                    const countryLabel =
                        this.selectedCountryCode?.text ||
                        this.phoneForm.countryCodeText ||
                        'selected country';
                    this.phoneError =
                        `Please enter a valid ${requiredLength}-digit phone number for ${countryLabel}.`;
                    return;
                }
                const formattedPhone =
                    this.formatPhoneByLength(
                        digits,
                        requiredLength
                    );
                this.phoneSubmitted = true;
                this.$emit('phone-submit', {
                    countryCodeId:
                        this.phoneForm.countryCodeId,
                    countryCodeText:
                        this.selectedCountryCode?.text ||
                        this.phoneForm.countryCodeText ||
                        '',
                    phoneNumber: formattedPhone
                });
            },
            confirmAdd() {
                if (this.submitted) {
                    return;
                }
                this.submitted = true;
                this.$emit('submit', {
                    ...this.prefilled,
                    countryCodeId:
                        this.prefilled.countryCodeId ||
                        this.phoneForm.countryCodeId ||
                        '',
                    countryCodeText:
                        (
                            this.prefilled.countryCodeText &&
                            this.prefilled.countryCodeText !== 'Loading…' &&
                            this.prefilled.countryCodeText !== 'Loading...'
                        )
                            ? this.prefilled.countryCodeText
                            : (
                                this.selectedCountryCodeText ||
                                this.selectedCountryCode?.text ||
                                this.phoneForm.countryCodeText ||
                                ''
                            )
                });
            },
            getRoommateName(roommate) {
                return (
                    roommate.name ||
                    roommate.shareholderName ||
                    roommate.roommateName ||
                    `${roommate.firstName || ''} ${roommate.lastName || ''}`.trim() ||
                    '-'
                );
            },
            getRoommateEmail(roommate) {
                return (
                    roommate.email ||
                    roommate.shareholderEmail ||
                    roommate.roommateEmail ||
                    '-'
                );
            },
            getRoommatePhone(roommate) {
                return (
                    roommate.phone ||
                    roommate.phoneNumber ||
                    roommate.shareholderPhoneNumber ||
                    '-'
                );
            },
            getRoommateShare(roommate) {
                return (
                    roommate.shareRank ||
                    roommate.ShareHolder ||
                    roommate.shareHolder ||
                    ''
                );
            },
            isPrimaryRoommate(roommate) {
                return (
                    roommate.isPrimary === true ||
                    roommate.isPrimary === 1 ||
                    roommate.IsPrimaryRoommate === 1 ||
                    roommate.isPrimaryRoommate === 1
                );
            },
            roommateKey(roommate, index) {
                return String(
                    roommate?.shareHistoryId ||
                    roommate?.ShareHistoryId ||
                    roommate?.id ||
                    roommate?.Id ||
                    index
                );
            },
            getRoommateInitials(roommate) {
                const name = String(
                    this.getRoommateName(roommate) || ''
                ).trim();
                if (!name || name === '-') {
                    return '?';
                }
                const parts = name
                    .split(/\s+/)
                    .filter(Boolean);
                if (parts.length >= 2) {
                    return (
                        parts[0].charAt(0) +
                        parts[1].charAt(0)
                    ).toUpperCase();
                }
                return parts[0]
                    .substring(0, 2)
                    .toUpperCase();
            },
            getAvatarColor(roommate, index) {
                const palette = this.avatarColorPalette;
                if (!palette || !palette.length) {
                    return '#0f66b8';
                }
                const key = this.roommateKey(roommate, index);
                let hash = 0;
                for (let i = 0; i < key.length; i++) {
                    hash = key.charCodeAt(i) + ((hash << 5) - hash);
                    hash |= 0;
                }
                const paletteIndex =
                    Math.abs(hash) % palette.length;
                return palette[paletteIndex];
            },
            toggleRoommateDetails(roommate, index) {
                const key =
                    this.roommateKey(
                        roommate,
                        index
                    );
                this.expandedRoommateKey =
                    this.expandedRoommateKey === key
                        ? ''
                        : key;
            },
            isRoommateDetailsOpen(roommate, index) {
                return (
                    this.expandedRoommateKey ===
                    this.roommateKey(
                        roommate,
                        index
                    )
                );
            },
            isRoommateActive(roommate) {
                const value =
                    roommate?.isActive ??
                    roommate?.IsActive ??
                    roommate?.active ??
                    roommate?.Active;
                if (
                    value === undefined ||
                    value === null ||
                    value === ''
                ) {
                    return true;
                }
                return (
                    value === true ||
                    value === 1 ||
                    String(value).toLowerCase() === 'true' ||
                    String(value).toLowerCase() === 'active'
                );
            },
            getRoommatePaymentDate(roommate) {
                return (
                    roommate?.paymentDate ||
                    roommate?.PaymentDate ||
                    roommate?.payment_date ||
                    roommate?.PaymentDateText ||
                    roommate?.createdOn ||
                    roommate?.CreatedOn ||
                    '-'
                );
            },
            showConfirmModal(
                title,
                message,
                action,
                roommate,
                newStatus = null
            ) {
                this.confirmModal = {
                    show: true,
                    title,
                    message,
                    action,
                    roommate,
                    newStatus
                };
            },
            closeConfirmModal() {
                this.confirmModal = {
                    show: false,
                    title: '',
                    message: '',
                    action: '',
                    roommate: null,
                    newStatus: null
                };
            },
            acceptConfirmModal() {
                const action =
                    this.confirmModal.action;
                const roommate =
                    this.confirmModal.roommate;
                const newStatus =
                    this.confirmModal.newStatus;
                this.closeConfirmModal();
                if (!roommate) {
                    return;
                }
                if (action === 'delete') {
                    this.$emit('delete-roommate', {
                        roommate: roommate,
                        assetID:
                            this.roommateData?.assetID ||
                            this.roommateData?.assetId ||
                            ''
                    });
                    return;
                }
                if (action === 'status') {
                    this.$emit('toggle-status', {
                        roommate: roommate,
                        isActive: newStatus,
                        assetID:
                            this.roommateData?.assetID ||
                            this.roommateData?.assetId ||
                            ''
                    });
                }
            },
            requestStatusToggle(roommate) {
                const currentStatus =
                    this.isRoommateActive(roommate);
                const newStatus =
                    !currentStatus;
                const name =
                    this.getRoommateName(roommate);
                this.showConfirmModal(
                    `Change ${this.entityLabel} Status`,
                    `Are you sure you want to mark "${name}" as ${newStatus ? 'active' : 'in-active'}?`,
                    'status',
                    roommate,
                    newStatus
                );
            },
            requestDelete(roommate) {
                const name =
                    this.getRoommateName(roommate);
                this.showConfirmModal(
                    `Delete ${this.entityLabel}`,
                    `Are you sure you want to delete ${this.entityLabel.toLowerCase()} "${name}"?`,
                    'delete',
                    roommate
                );
            }
        }
    };
</script>

<style scoped>
    .roommate-flow-wrapper {
        width: 100%;
        font-family: Outfit, sans-serif;
    }

    /* =========================================================
       COMMON CARD
       ========================================================= */
    .roommate-card {
        width: 100%;
        min-width: 300px;
        max-width: 360px;
        background: #ffffff;
        border-radius: 16px;
        overflow: hidden;
        font-size: 13px;
        box-sizing: border-box;
        box-shadow: 0 4px 18px rgba(0, 0, 0, 0.12);
    }

    .roommate-card-header {
        background: linear-gradient(135deg, #0f66b8 0%, #122341 100%);
        color: #ffffff;
        padding: 14px 18px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 10px;
    }

    .error-header {
        background: linear-gradient(135deg, #991b1b, #dc2626) !important;
    }

    .roommate-card-title {
        margin: 0;
        font-size: 14px;
        font-weight: 700;
        color: #ffffff;
    }

    .roommate-card-subtitle {
        margin: 2px 0 0;
        font-size: 11px;
        line-height: 1.4;
        opacity: 0.8;
        color: #ffffff;
    }

    .roommate-card-body {
        padding: 14px 18px;
    }

    /* =========================================================
       PHONE CARD
       ========================================================= */
    .roommate-phone-card {
        width: 330px;
        max-width: 330px;
        background: #ffffff;
        color: #333333;
        border-radius: 16px;
        overflow: visible;
        font-family: Outfit, sans-serif;
        font-size: 13px;
        box-sizing: border-box;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    }

    .roommate-phone-message {
        width: 100%;
        padding: 12px 16px;
        background: linear-gradient(135deg, #0f66b8 0%, #122341 100%);
        color: #ffffff;
        font-size: 13px;
        font-weight: 600;
        line-height: 1.4;
        box-sizing: border-box;
        border-bottom: 2px solid #f39c12;
        border-radius: 16px 16px 0 0;
    }

    .roommate-phone-row {
        display: flex;
        align-items: center;
        gap: 8px;
        width: 100%;
        padding: 16px 16px 10px;
        box-sizing: border-box;
        background: #ffffff;
        border-radius: 0;
    }

    .roommate-country-dropdown {
        width: 125px;
        min-width: 125px;
        max-width: 125px;
        flex: 0 0 125px;
    }

    .roommate-phone-input {
        flex: 1;
        width: 100%;
        min-width: 0;
        height: 40px;
        padding: 8px 10px;
        border: 1px solid #d7dce2;
        border-radius: 8px;
        background: #ffffff;
        color: #1e2d45;
        font-family: Outfit, sans-serif;
        font-size: 13px;
        outline: none;
        box-sizing: border-box;
    }

        .roommate-phone-input:focus {
            border-color: #0f66b8;
            box-shadow: 0 0 0 2px rgba(15, 102, 184, 0.12);
        }

    .roommate-phone-error {
        padding: 0 16px;
        margin: 0 0 5px;
        color: #dc2626;
        font-size: 11px;
    }

    .roommate-phone-submit {
        width: calc(100% - 32px);
        min-height: 40px;
        margin: 4px 16px 16px;
        border: none;
        border-radius: 10px;
        background: linear-gradient(135deg, #0f66b8 0%, #122341 100%);
        color: #ffffff;
        font-size: 13px;
        font-weight: 700;
        cursor: pointer;
    }

        .roommate-phone-submit:disabled {
            opacity: 0.45;
            cursor: not-allowed;
        }

    /* =========================================================
       PRIMEVUE DROPDOWN
       ========================================================= */
    :deep(.roommate-country-dropdown.p-dropdown) {
        width: 125px;
        min-width: 125px;
        max-width: 125px;
        height: 40px;
        border: 1px solid #d7dce2;
        border-radius: 8px;
        background: #ffffff;
        box-shadow: none;
    }

    :deep(.roommate-country-dropdown .p-dropdown-label) {
        padding: 9px 7px 9px 10px;
        font-size: 13px;
        color: #1e2d45;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }

    :deep(.roommate-country-dropdown .p-dropdown-trigger) {
        width: 28px;
        min-width: 28px;
    }

    :deep(.p-dropdown-panel) {
        min-width: 180px !important;
        max-width: 240px !important;
        z-index: 99999 !important;
    }

    :deep(.p-dropdown-items-wrapper) {
        max-height: 210px !important;
    }

    :deep(.p-dropdown-item) {
        padding: 8px 10px;
        font-size: 12px;
        white-space: nowrap;
    }

    /* =========================================================
       SUMMARY ROWS
       ========================================================= */
    .roommate-row {
        display: flex;
        justify-content: space-between;
        gap: 10px;
        padding-bottom: 8px;
        margin-bottom: 8px;
        border-bottom: 1px solid #f0f0f0;
    }

        .roommate-row.no-border {
            border-bottom: none;
            margin-bottom: 0;
            padding-bottom: 0;
        }

    .roommate-label {
        color: #888888;
        font-size: 11px;
        font-weight: 600;
    }

    .roommate-value {
        flex: 1;
        color: #1e2d45;
        font-size: 12px;
        font-weight: 600;
        text-align: right;
        word-break: break-word;
    }

    /* =========================================================
       SUMMARY ACTIONS
       ========================================================= */
    .roommate-card-actions {
        padding: 0 18px 16px;
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .btn-primary-roommate {
        width: 100%;
        min-height: 40px;
        background: linear-gradient(135deg, #0f66b8 0%, #122341 100%);
        color: #ffffff;
        border: none;
        border-radius: 10px;
        padding: 10px;
        font-size: 13px;
        font-weight: 700;
        cursor: pointer;
    }

        .btn-primary-roommate:disabled,
        .btn-cancel-roommate:disabled,
        .remove-roommate-btn:disabled {
            opacity: 0.45;
            cursor: not-allowed;
            pointer-events: none;
        }

    .btn-cancel-roommate {
        width: 100%;
        border: none;
        background: transparent;
        color: #aaaaaa;
        font-size: 12px;
        cursor: pointer;
        text-decoration: underline;
        padding: 4px 8px;
    }

    /* =========================================================
       ROOMMATE COUNT
       ========================================================= */
    .roommate-count {
        margin-left: auto;
        min-width: 28px;
        height: 28px;
        padding: 0 7px;
        border: 1px solid rgba(255, 255, 255, 0.35);
        border-radius: 14px;
        display: flex;
        justify-content: center;
        align-items: center;
        box-sizing: border-box;
        color: #ffffff;
        font-size: 11px;
        font-weight: 600;
    }

    .roommate-count-orange {
        background: #f5a000;
        border: 0;
        color: #111827;
        font-weight: 700;
    }

    /* =========================================================
       ROOMMATE LIST
       ========================================================= */
    .roommate-list-card {
        max-width: 360px;
    }

    .roommate-list {
        padding: 0 16px 8px;
    }

    .roommate-list-item {
        display: block;
        padding: 11px 0;
        border-bottom: 1px solid #e5e7eb;
    }

        .roommate-list-item:last-child {
            border-bottom: none;
        }

    /* =========================================================
       ROOMMATE TOP ROW
       ========================================================= */
    .roommate-list-top {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 8px;
    }

    .roommate-list-main {
        flex: 1;
        min-width: 0;
    }

    .roommate-name-row {
        display: flex;
        align-items: center;
        gap: 10px;
        min-width: 0;
    }

    .roommate-avatar {
        flex: 0 0 34px;
        width: 34px;
        height: 34px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #ffffff;
        font-size: 12px;
        font-weight: 700;
        text-transform: uppercase;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
    }

    .roommate-name-info {
        flex: 1;
        min-width: 0;
    }

    .roommate-name {
        color: #1e2d45;
        font-size: 13px;
        line-height: 1.35;
        font-weight: 700;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    /* =========================================================
       BADGES
       ========================================================= */
    .roommate-badges {
        display: flex;
        align-items: center;
        gap: 6px;
        flex-wrap: wrap;
        margin-top: 6px;
    }

    .primary-badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-height: 20px;
        padding: 2px 9px;
        box-sizing: border-box;
        background: #ffffff;
        border: 1px solid #b7dfc9;
        border-radius: 12px;
        color: #16794b;
        font-size: 10px;
        font-weight: 600;
        line-height: 1.2;
        white-space: nowrap;
    }

    /* =========================================================
       DETAILS / REMOVE
       ========================================================= */
    .roommate-list-actions {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-shrink: 0;
    }

    .roommate-details-btn {
        border: none;
        background: transparent;
        color: #d78a00;
        padding: 0;
        font-size: 10px;
        font-weight: 700;
        cursor: pointer;
    }

        .roommate-details-btn:disabled {
            opacity: 0.45;
            cursor: not-allowed;
        }

    .roommate-details-arrow {
        font-size: 8px;
        margin-left: 2px;
    }

    .remove-roommate-btn {
        background: transparent;
        border: none;
        color: #dc2626;
        padding: 0;
        font-size: 10px;
        font-weight: 600;
        cursor: pointer;
    }

        .remove-roommate-btn:disabled {
            opacity: 0.45;
            cursor: not-allowed;
        }

    /* =========================================================
       EXPANDED ROOMMATE DETAILS
       ========================================================= */
    .roommate-details {
        margin-top: 10px;
        padding: 9px 10px;
        background: #f8fafc;
        border: 1px solid #dbe3ec;
        border-radius: 7px;
        box-sizing: border-box;
    }

    .roommate-detail-row {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        padding: 6px 0;
        font-size: 11px;
        line-height: 1.35;
        border-bottom: 1px solid #e5e7eb;
    }

        .roommate-detail-row:last-child {
            border-bottom: none;
            padding-bottom: 2px;
        }

    .roommate-detail-label {
        width: 82px;
        min-width: 82px;
        color: #64748b;
        font-size: 11px;
        font-weight: 600;
    }

    .roommate-detail-value {
        flex: 1;
        min-width: 0;
        color: #1e2d45;
        font-size: 11px;
        font-weight: 500;
        overflow-wrap: anywhere;
    }

    /* =========================================================
       EMPTY STATE
       ========================================================= */
    .empty-state {
        text-align: center;
        padding: 18px;
        color: #999999;
        font-size: 12px;
    }

    /* =========================================================
       ERROR
       ========================================================= */
    .roommate-error {
        display: flex;
        gap: 8px;
        margin-top: 8px;
        padding: 10px;
        background: #fef2f2;
        border: 1px solid #fecaca;
        border-radius: 8px;
        color: #dc2626;
        font-size: 12px;
    }

    /* =========================================================
       RESULT FOOTER
       ========================================================= */
    .roommate-result-footer {
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-top: 1px solid #f0f0f0;
        padding: 10px 18px;
        color: #64748b;
        font-size: 11px;
    }

    .status-success {
        color: #16a34a;
        font-weight: 600;
    }

    .status-error {
        color: #dc2626;
        font-weight: 600;
    }

    /* =========================================================
       CONFIRMATION POPUP
       ========================================================= */
    .roommate-confirm-overlay {
        position: fixed;
        inset: 0;
        z-index: 999999;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 20px;
        box-sizing: border-box;
        background: rgba(0, 0, 0, 0.48);
    }

    .roommate-confirm-card {
        width: min(830px, calc(100vw - 40px));
        max-width: 830px;
        background: #ffffff;
        border-radius: 3px;
        overflow: hidden;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.28);
        text-align: left;
    }

    .roommate-confirm-header {
        min-height: 42px;
        display: flex;
        align-items: center;
        padding: 10px 14px;
        box-sizing: border-box;
        background: #132a4e;
        color: #ffffff;
        font-size: 16px;
        font-weight: 600;
    }

    .roommate-confirm-body {
        padding: 20px 14px 10px;
        background: #ffffff;
    }

    .roommate-confirm-message {
        margin: 0;
        color: #222222;
        font-size: 14px;
        line-height: 1.5;
    }

    .roommate-confirm-actions {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 9px;
        margin-top: 20px;
    }

    .roommate-confirm-btn {
        min-width: 90px;
        height: 34px;
        padding: 6px 16px;
        border: 0;
        border-radius: 3px;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
    }

    .roommate-confirm-ok {
        background: #132a4e;
        color: #ffffff;
    }

    .roommate-confirm-cancel {
        background: #cc3b30;
        color: #ffffff;
    }

    /* =========================================================
       MOBILE
       ========================================================= */
    @media (max-width: 480px) {
        .roommate-list-actions {
            gap: 6px;
        }

        .roommate-confirm-overlay {
            padding: 12px;
        }

        .roommate-confirm-card {
            width: 100%;
        }

        .roommate-confirm-header {
            font-size: 14px;
        }

        .roommate-confirm-message {
            font-size: 13px;
        }

        .roommate-card,
        .roommate-phone-card {
            min-width: 0;
            width: 100%;
            max-width: 330px;
        }

        .roommate-avatar {
            flex: 0 0 30px;
            width: 30px;
            height: 30px;
            font-size: 11px;
        }

        .roommate-country-dropdown {
            width: 115px;
            min-width: 115px;
            max-width: 115px;
            flex-basis: 115px;
        }

        :deep(.roommate-country-dropdown.p-dropdown) {
            width: 115px;
            min-width: 115px;
            max-width: 115px;
        }
    }
</style>